import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiEnvelope, Role } from '../../models/auth.dto';
import {
  ChangeRoleRequest,
  CompanyResponse,
  CompanyStatus,
  CreateUserRequest,
  DisableUserRequest,
  SuspendCompanyRequest,
  UserResponse,
} from '../../models/company.dto';
import { PlatformWalletResponse, TopUpRequest, WalletResponse } from '../../models/wallet.dto';
import {
  CountryBlockchainMappingRequest,
  CountryBlockchainMappingResponse,
  CountryEventTemplateRequest,
  CountryEventTemplateResponse,
  CountryTermRuleRequest,
  CountryTermRuleResponse,
  GeneralTermDescriptionRequest,
  GeneralTermRequest,
  GeneralTermResponse,
  TemplateScope,
} from '../../models/country-config.dto';
import { AuditEventResponse, AuditEventType, AuditOutcome } from '../../models/audit-event.dto';
import {
  AdminExportRequestResponse,
  ExportRequestStatsResponse,
  ExportRequestStatus,
} from '../../models/export-request.dto';

export interface AdminExportRequestFilters {
  status?: ExportRequestStatus;
  projectId?: string;
  companyId?: string;
}

export interface AuditEventFilters {
  type?: AuditEventType;
  outcome?: AuditOutcome;
  companyId?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private apiUrl = environment.cbdx_service.api_path;

  constructor(private http: HttpClient) {}

  listCompanies(status?: CompanyStatus, page = 0, size = 20): Observable<ApiEnvelope<CompanyResponse[]>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<ApiEnvelope<CompanyResponse[]>>(`${this.apiUrl}/admin/companies`, { params });
  }

  pendingCompanyCount(): Observable<ApiEnvelope<CompanyResponse[]>> {
    return this.listCompanies('PENDING', 0, 1);
  }

  getCompany(id: string): Observable<ApiEnvelope<CompanyResponse>> {
    return this.http.get<ApiEnvelope<CompanyResponse>>(`${this.apiUrl}/admin/companies/${id}`);
  }

  listCompanyUsers(companyId: string): Observable<ApiEnvelope<UserResponse[]>> {
    return this.http.get<ApiEnvelope<UserResponse[]>>(`${this.apiUrl}/admin/companies/${companyId}/users`);
  }

  approve(id: string): Observable<ApiEnvelope<CompanyResponse>> {
    return this.http.post<ApiEnvelope<CompanyResponse>>(`${this.apiUrl}/admin/companies/${id}/approve`, {});
  }

  reject(id: string, reason: string): Observable<ApiEnvelope<CompanyResponse>> {
    return this.http.post<ApiEnvelope<CompanyResponse>>(`${this.apiUrl}/admin/companies/${id}/reject`, { reason });
  }

  suspend(id: string, request: SuspendCompanyRequest): Observable<ApiEnvelope<CompanyResponse>> {
    return this.http.post<ApiEnvelope<CompanyResponse>>(`${this.apiUrl}/admin/companies/${id}/suspend`, request);
  }

  reactivate(id: string): Observable<ApiEnvelope<CompanyResponse>> {
    return this.http.post<ApiEnvelope<CompanyResponse>>(`${this.apiUrl}/admin/companies/${id}/reactivate`, {});
  }

  listUsers(role?: Role, page = 0, size = 20): Observable<ApiEnvelope<UserResponse[]>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (role) {
      params = params.set('role', role);
    }
    return this.http.get<ApiEnvelope<UserResponse[]>>(`${this.apiUrl}/admin/users`, { params });
  }

  createUser(request: CreateUserRequest): Observable<ApiEnvelope<UserResponse>> {
    return this.http.post<ApiEnvelope<UserResponse>>(`${this.apiUrl}/admin/users`, request);
  }

  disableUser(id: string, request: DisableUserRequest): Observable<ApiEnvelope<UserResponse>> {
    return this.http.post<ApiEnvelope<UserResponse>>(`${this.apiUrl}/admin/users/${id}/disable`, request);
  }

  enableUser(id: string): Observable<ApiEnvelope<UserResponse>> {
    return this.http.post<ApiEnvelope<UserResponse>>(`${this.apiUrl}/admin/users/${id}/enable`, {});
  }

  changeRole(id: string, request: ChangeRoleRequest): Observable<ApiEnvelope<UserResponse>> {
    return this.http.post<ApiEnvelope<UserResponse>>(`${this.apiUrl}/admin/users/${id}/role`, request);
  }

  topUpWallet(companyId: string, request: TopUpRequest): Observable<ApiEnvelope<WalletResponse>> {
    return this.http.post<ApiEnvelope<WalletResponse>>(
      `${this.apiUrl}/admin/companies/${companyId}/wallet/top-up`,
      request
    );
  }

  platformWallet(): Observable<ApiEnvelope<PlatformWalletResponse[]>> {
    return this.http.get<ApiEnvelope<PlatformWalletResponse[]>>(`${this.apiUrl}/admin/platform-wallet`);
  }

  platformWalletByNetwork(network: string): Observable<ApiEnvelope<PlatformWalletResponse>> {
    return this.http.get<ApiEnvelope<PlatformWalletResponse>>(`${this.apiUrl}/admin/platform-wallet/${network}`);
  }

  listCountryMappings(): Observable<ApiEnvelope<CountryBlockchainMappingResponse[]>> {
    return this.http.get<ApiEnvelope<CountryBlockchainMappingResponse[]>>(
      `${this.apiUrl}/admin/country-blockchain-mappings`
    );
  }

  upsertCountryMapping(
    countryCode: string,
    request: CountryBlockchainMappingRequest
  ): Observable<ApiEnvelope<CountryBlockchainMappingResponse>> {
    return this.http.put<ApiEnvelope<CountryBlockchainMappingResponse>>(
      `${this.apiUrl}/admin/country-blockchain-mappings/${countryCode}`,
      request
    );
  }

  listTermRules(countryCode?: string): Observable<ApiEnvelope<CountryTermRuleResponse[]>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get<ApiEnvelope<CountryTermRuleResponse[]>>(`${this.apiUrl}/admin/country-term-rules`, {
      params,
    });
  }

  upsertTermRule(
    countryCode: string,
    term: string,
    eventType: string | undefined,
    request: CountryTermRuleRequest
  ): Observable<ApiEnvelope<CountryTermRuleResponse>> {
    let params = new HttpParams();
    if (eventType) {
      params = params.set('eventType', eventType);
    }
    return this.http.put<ApiEnvelope<CountryTermRuleResponse>>(
      `${this.apiUrl}/admin/country-term-rules/${countryCode}/${term}`,
      request,
      { params }
    );
  }

  deleteTermRule(countryCode: string, term: string, eventType?: string): Observable<void> {
    let params = new HttpParams();
    if (eventType) {
      params = params.set('eventType', eventType);
    }
    return this.http.delete<void>(`${this.apiUrl}/admin/country-term-rules/${countryCode}/${term}`, { params });
  }

  listEventTemplates(
    countryCode?: string,
    scope?: TemplateScope
  ): Observable<ApiEnvelope<CountryEventTemplateResponse[]>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    if (scope) {
      params = params.set('scope', scope);
    }
    return this.http.get<ApiEnvelope<CountryEventTemplateResponse[]>>(
      `${this.apiUrl}/admin/country-event-templates`,
      { params }
    );
  }

  upsertEventTemplate(
    countryCode: string,
    scope: TemplateScope,
    eventType: string | undefined,
    request: CountryEventTemplateRequest
  ): Observable<ApiEnvelope<CountryEventTemplateResponse>> {
    let params = new HttpParams().set('scope', scope);
    if (eventType) {
      params = params.set('eventType', eventType);
    }
    return this.http.put<ApiEnvelope<CountryEventTemplateResponse>>(
      `${this.apiUrl}/admin/country-event-templates/${countryCode}`,
      request,
      { params }
    );
  }

  deleteEventTemplate(countryCode: string, scope: TemplateScope, eventType?: string): Observable<void> {
    let params = new HttpParams().set('scope', scope);
    if (eventType) {
      params = params.set('eventType', eventType);
    }
    return this.http.delete<void>(`${this.apiUrl}/admin/country-event-templates/${countryCode}`, { params });
  }

  listGeneralTerms(): Observable<ApiEnvelope<GeneralTermResponse[]>> {
    return this.http.get<ApiEnvelope<GeneralTermResponse[]>>(`${this.apiUrl}/admin/general-terms`);
  }

  createGeneralTerm(request: GeneralTermRequest): Observable<ApiEnvelope<GeneralTermResponse>> {
    return this.http.post<ApiEnvelope<GeneralTermResponse>>(`${this.apiUrl}/admin/general-terms`, request);
  }

  updateGeneralTerm(
    id: number,
    request: GeneralTermDescriptionRequest
  ): Observable<ApiEnvelope<GeneralTermResponse>> {
    return this.http.put<ApiEnvelope<GeneralTermResponse>>(`${this.apiUrl}/admin/general-terms/${id}`, request);
  }

  deleteGeneralTerm(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/admin/general-terms/${id}`);
  }

  listAuditEvents(
    filters: AuditEventFilters = {},
    page = 0,
    size = 50
  ): Observable<ApiEnvelope<AuditEventResponse[]>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (filters.type) {
      params = params.set('type', filters.type);
    }
    if (filters.outcome) {
      params = params.set('outcome', filters.outcome);
    }
    if (filters.companyId) {
      params = params.set('companyId', filters.companyId);
    }
    if (filters.actorId) {
      params = params.set('actorId', filters.actorId);
    }
    if (filters.from) {
      params = params.set('from', filters.from);
    }
    if (filters.to) {
      params = params.set('to', filters.to);
    }
    return this.http.get<ApiEnvelope<AuditEventResponse[]>>(`${this.apiUrl}/admin/audit-events`, { params });
  }

  listExportRequests(
    filters: AdminExportRequestFilters = {},
    page = 0,
    size = 20
  ): Observable<ApiEnvelope<AdminExportRequestResponse[]>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.projectId) {
      params = params.set('projectId', filters.projectId);
    }
    if (filters.companyId) {
      params = params.set('companyId', filters.companyId);
    }
    return this.http.get<ApiEnvelope<AdminExportRequestResponse[]>>(`${this.apiUrl}/admin/export-requests`, {
      params,
    });
  }

  exportRequestStats(): Observable<ApiEnvelope<ExportRequestStatsResponse>> {
    return this.http.get<ApiEnvelope<ExportRequestStatsResponse>>(`${this.apiUrl}/admin/export-requests/stats`);
  }
}
